import React, { useState } from 'react';
import PhoenixRisingSystem from './components/PhoenixRisingSystem';
import PhoenixDashboard from './components/PhoenixDashboard';
import { Home, BarChart3, DollarSign, Rocket, Settings } from 'lucide-react';

interface AppState {
  currentView: 'phoenix' | 'analytics' | 'revenue' | 'deployment';
  isDeploying: boolean;
  deploymentProgress: number;
}

function App() {
  const [appState, setAppState] = useState<AppState>({
    currentView: 'phoenix',
    isDeploying: false,
    deploymentProgress: 0
  });

  const startDeployment = () => {
    setAppState(prev => ({ ...prev, isDeploying: true, deploymentProgress: 0 }));
    
    const interval = setInterval(() => {
      setAppState(prev => {
        if (prev.deploymentProgress >= 100) {
          clearInterval(interval);
          return { ...prev, isDeploying: false, deploymentProgress: 100 };
        }
        return { ...prev, deploymentProgress: prev.deploymentProgress + 1 };
      });
    }, 200);
  };

  const renderView = () => {
    switch (appState.currentView) {
      case 'phoenix':
        return <PhoenixRisingSystem />;
      case 'analytics':
        return (
          <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 text-white p-8">
            <div className="container mx-auto">
              <h1 className="text-4xl font-bold mb-8 flex items-center">
                <BarChart3 className="w-8 h-8 mr-3" />
                Analytics Dashboard
              </h1>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-black/20 backdrop-blur-sm rounded-lg p-6 border border-white/10">
                  <h3 className="text-xl font-semibold mb-4">Content Performance</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Articles Generated</span>
                      <span className="font-bold text-green-400">1,247</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Social Posts</span>
                      <span className="font-bold text-blue-400">892</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Engagement Rate</span>
                      <span className="font-bold text-purple-400">78.5%</span>
                    </div>
                  </div>
                </div>
                <div className="bg-black/20 backdrop-blur-sm rounded-lg p-6 border border-white/10">
                  <h3 className="text-xl font-semibold mb-4">AI Usage</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Claude API Calls</span>
                      <span className="font-bold text-green-400">2,341</span>
                    </div>
                    <div className="flex justify-between">
                      <span>OpenAI Calls</span>
                      <span className="font-bold text-blue-400">1,156</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Gemini Calls</span>
                      <span className="font-bold text-purple-400">987</span>
                    </div>
                  </div>
                </div>
                <div className="bg-black/20 backdrop-blur-sm rounded-lg p-6 border border-white/10">
                  <h3 className="text-xl font-semibold mb-4">System Health</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Uptime</span>
                      <span className="font-bold text-green-400">99.9%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Response Time</span>
                      <span className="font-bold text-blue-400">1.2s</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Error Rate</span>
                      <span className="font-bold text-red-400">0.1%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'revenue':
        return (
          <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 text-white p-8">
            <div className="container mx-auto">
              <h1 className="text-4xl font-bold mb-8 flex items-center">
                <DollarSign className="w-8 h-8 mr-3" />
                Revenue Dashboard
              </h1>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-black/20 backdrop-blur-sm rounded-lg p-6 border border-white/10">
                  <h3 className="text-xl font-semibold mb-4">Payment Processing</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Stripe Revenue</span>
                      <span className="font-bold text-green-400">$12,450</span>
                    </div>
                    <div className="flex justify-between">
                      <span>PayPal Revenue</span>
                      <span className="font-bold text-blue-400">$8,230</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Success Rate</span>
                      <span className="font-bold text-purple-400">98.7%</span>
                    </div>
                  </div>
                </div>
                <div className="bg-black/20 backdrop-blur-sm rounded-lg p-6 border border-white/10">
                  <h3 className="text-xl font-semibold mb-4">Affiliate System</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Active Affiliates</span>
                      <span className="font-bold text-green-400">156</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Commission Paid</span>
                      <span className="font-bold text-blue-400">$3,420</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Conversion Rate</span>
                      <span className="font-bold text-purple-400">12.3%</span>
                    </div>
                  </div>
                </div>
                <div className="bg-black/20 backdrop-blur-sm rounded-lg p-6 border border-white/10">
                  <h3 className="text-xl font-semibold mb-4">Monthly Overview</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Total Revenue</span>
                      <span className="font-bold text-green-400">$20,680</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Growth Rate</span>
                      <span className="font-bold text-blue-400">+23.5%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Profit Margin</span>
                      <span className="font-bold text-purple-400">67.8%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'deployment':
        return <PhoenixDashboard />;
      default:
        return <PhoenixRisingSystem />;
    }
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-white">🔥 Phoenix Rising</h1>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => setAppState(prev => ({ ...prev, currentView: 'phoenix' }))}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  appState.currentView === 'phoenix'
                    ? 'bg-yellow-500 text-black'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                <Home className="w-4 h-4 inline mr-1" />
                Phoenix
              </button>
              <button
                onClick={() => setAppState(prev => ({ ...prev, currentView: 'analytics' }))}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  appState.currentView === 'analytics'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                <BarChart3 className="w-4 h-4 inline mr-1" />
                Analytics
              </button>
              <button
                onClick={() => setAppState(prev => ({ ...prev, currentView: 'revenue' }))}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  appState.currentView === 'revenue'
                    ? 'bg-green-500 text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                <DollarSign className="w-4 h-4 inline mr-1" />
                Revenue
              </button>
              <button
                onClick={() => setAppState(prev => ({ ...prev, currentView: 'deployment' }))}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  appState.currentView === 'deployment'
                    ? 'bg-purple-500 text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                <Rocket className="w-4 h-4 inline mr-1" />
                Deployment
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      {renderView()}
    </div>
  );
}

export default App; 